import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, ChevronLeft, ChevronDown, CheckCircle2, XCircle, AlertTriangle,
  Clock, Building2, Filter, RefreshCw,
} from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import { listVerifications, getVerificationsSummary, reviewVerification } from "../../api/face";
import { listBranches } from "../../api/branches";
import { unwrapList, errorMessage, formatDate, formatDateTime } from "../../lib/records";
import { Button, Avatar } from "../../components/ui";

const DECISION_STYLE = {
  CONFIRMED:    { bg: "bg-emerald-100", text: "text-emerald-800", icon: CheckCircle2,  label: "Confirmed" },
  REJECTED:     { bg: "bg-red-100",     text: "text-red-800",     icon: XCircle,        label: "Rejected" },
  INCONCLUSIVE: { bg: "bg-gray-100",    text: "text-gray-700",    icon: AlertTriangle,  label: "Inconclusive" },
};

function SimilarityBar({ value }) {
  if (value == null) return <span className="text-gray-400 text-xs">—</span>;
  const pct = Math.max(0, Math.min(100, ((value + 1) / 2) * 100));
  const color = value >= 0.40 ? "bg-emerald-500" : value >= 0.20 ? "bg-amber-400" : "bg-red-400";
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-mono font-semibold ${value >= 0.40 ? "text-emerald-600" : value >= 0.20 ? "text-amber-600" : "text-red-500"}`}>
        {value.toFixed(2)}
      </span>
    </div>
  );
}

function EmployeeVerificationGroups({ items, onReview }) {
  const [openEmp, setOpenEmp] = useState(null);

  // Group by employee id, preserving insertion order
  const groups = [];
  const seen = {};
  items.forEach(item => {
    const empId = item.employee?._id ?? "unknown";
    if (!seen[empId]) {
      seen[empId] = { emp: item.employee ?? {}, items: [] };
      groups.push(seen[empId]);
    }
    seen[empId].items.push(item);
  });

  return (
    <div className="space-y-2">
      {groups.map(group => {
        const empId = group.emp._id ?? "unknown";
        const isOpen = openEmp === empId;
        const pendingCount = group.items.filter(i => !i.decision || i.decision === "PENDING").length;

        return (
          <div key={empId} className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
            {/* Accordion header */}
            <button
              type="button"
              onClick={() => setOpenEmp(isOpen ? null : empId)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50/60 transition text-left"
            >
              <Avatar
                name={group.emp.name ?? "?"}
                size={36}
                className="shrink-0 bg-gradient-to-br from-emerald-500 to-green-600 text-white text-sm"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-gray-900">{group.emp.name ?? "—"}</span>
                  {group.emp.designation && (
                    <span className="text-xs text-gray-400">{group.emp.designation}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-gray-500">
                    {group.items.length} verification{group.items.length !== 1 ? "s" : ""}
                  </span>
                  {pendingCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 bg-amber-100 text-amber-800">
                      <Clock size={9} /> {pendingCount} pending
                    </span>
                  )}
                </div>
              </div>
              <ChevronDown
                size={16}
                className={`text-gray-400 transition-transform duration-200 shrink-0 ${isOpen ? "rotate-180" : ""}`}
              />
            </button>

            {/* Accordion body */}
            {isOpen && (
              <div className="divide-y divide-gray-50 border-t border-gray-100">
                {group.items.map(item => {
                  const reviewed = item.decision && item.decision !== "PENDING";
                  const ds = DECISION_STYLE[item.decision] ?? null;
                  return (
                    <div key={item._id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50/40 transition">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {ds ? (
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-0.5 ${ds.bg} ${ds.text}`}>
                              <ds.icon size={10} /> {ds.label}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-0.5 bg-amber-100 text-amber-800">
                              <Clock size={10} /> Pending
                            </span>
                          )}
                          {item.mockLocation && (
                            <span className="text-xs text-red-500 flex items-center gap-0.5">
                              <AlertTriangle size={10} /> Mock GPS
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <SimilarityBar value={item.similarity} />
                          <span className="text-xs text-gray-400">{formatDateTime(item.createdAt)}</span>
                        </div>
                      </div>
                      {!reviewed && (
                        <Button
                          onClick={() => onReview(item)}
                          className="shrink-0 bg-emerald-500 text-white text-xs px-3 py-1.5"
                        >
                          Review
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ReviewModal({ item, onClose, onDone }) {
  const [decision, setDecision] = useState("CONFIRMED");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    setSaving(true);
    setError("");
    try {
      await reviewVerification(item._id, { decision, ...(note.trim() ? { note: note.trim() } : {}) });
      toast.success("Verification reviewed");
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const emp = item.employee ?? {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-500 to-green-600 px-5 py-4">
          <h2 className="text-base font-bold text-white flex items-center gap-2">
            <ShieldCheck size={18} /> Review Verification
          </h2>
          <p className="text-xs text-emerald-100 mt-0.5">{emp.name ?? "Employee"} · {formatDateTime(item.createdAt)}</p>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">{error}</div>
          )}

          {/* Face photo if available */}
          {item.photoUrl && (
            <img src={item.photoUrl} alt="Face capture" className="mx-auto h-40 w-40 rounded-xl object-cover border-2 border-gray-200 shadow" />
          )}

          {/* Similarity score */}
          <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 flex items-center justify-between">
            <span className="text-sm text-gray-600 font-medium">Similarity score</span>
            <SimilarityBar value={item.similarity} />
          </div>

          {/* Flags */}
          <div className="flex flex-wrap gap-2">
            {item.mockLocation && (
              <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 border border-red-200 px-2.5 py-1 rounded-full">
                <AlertTriangle size={11} /> Mock location
              </span>
            )}
            {item.livenessFailed && (
              <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1 rounded-full">
                <AlertTriangle size={11} /> Liveness failed
              </span>
            )}
          </div>

          {/* Decision */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Decision</p>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(DECISION_STYLE).map(([key, s]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setDecision(key)}
                  className={`rounded-xl border-2 p-2.5 text-xs font-semibold transition flex flex-col items-center gap-1 ${
                    decision === key
                      ? `${s.bg} ${s.text} border-current`
                      : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <s.icon size={16} />
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Note (optional)"
            rows={2}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          />
        </div>

        <div className="flex gap-2 px-5 pb-5">
          <Button variant="ghost" onClick={onClose} className="flex-1 border border-gray-200 text-gray-600">Cancel</Button>
          <Button
            onClick={submit}
            loading={saving}
            className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 text-white text-sm"
          >
            Submit review
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function FaceVerifications({ onSwitchView } = {}) {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [pendingOnly, setPendingOnly] = useState(true);
  const [decisionFilter, setDecisionFilter] = useState("");

  const [reviewTarget, setReviewTarget] = useState(null);

  useEffect(() => {
    if (isChain) {
      listBranches({ limit: 100 }).then(r => setBranches(unwrapList(r).items)).catch(() => {});
    }
  }, [isChain]);

  const loadSummary = useCallback(async () => {
    try {
      const res = await getVerificationsSummary();
      setSummary(res?.data ?? null);
    } catch { /* non-fatal */ }
  }, []);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 20 };
      if (branchFilter) params.partner = branchFilter;
      if (pendingOnly) params.pending = true;
      if (decisionFilter) params.decision = decisionFilter;
      const res = await listVerifications(params);
      const { items: rows, pagination: pg } = unwrapList(res);
      setItems(rows);
      setPagination(pg);
      setPage(p);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [branchFilter, pendingOnly, decisionFilter]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { load(1); }, [load]);

  function done(clearFn) { clearFn(); load(page); loadSummary(); }

  return (
    <div className="space-y-5 p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => onSwitchView ? onSwitchView("enrollment") : navigate("/face")}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 transition"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck size={22} className="text-emerald-600" />
            Face Verification Queue
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Review flagged or failed face checks from attendance punches</p>
        </div>
      </div>

      {/* Summary badges */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Pending review", value: summary.pending ?? 0, color: "text-amber-700", bg: "bg-amber-50 border-amber-200" },
            { label: "Confirmed", value: summary.confirmed ?? 0, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
            { label: "Rejected", value: summary.rejected ?? 0, color: "text-red-700", bg: "bg-red-50 border-red-200" },
            { label: "Inconclusive", value: summary.inconclusive ?? 0, color: "text-gray-700", bg: "bg-gray-50 border-gray-200" },
          ].map(c => (
            <div key={c.label} className={`rounded-xl border p-3 ${c.bg}`}>
              <p className="text-xs text-gray-500 font-medium">{c.label}</p>
              <p className={`text-2xl font-bold tabular-nums mt-0.5 ${c.color}`}>{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="rounded-xl bg-white border border-gray-100 shadow-sm p-3 flex flex-wrap items-center gap-2">
        {isChain && branches.length > 0 && (
          <div className="flex items-center gap-1.5">
            <Building2 size={14} className="text-gray-400" />
            <select
              value={branchFilter}
              onChange={e => setBranchFilter(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 bg-white"
            >
              <option value="">All branches</option>
              {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
            </select>
          </div>
        )}
        <button
          onClick={() => setPendingOnly(v => !v)}
          className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition ${
            pendingOnly
              ? "bg-amber-50 border-amber-300 text-amber-700"
              : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
          }`}
        >
          <Clock size={13} /> Pending only
        </button>
        {!pendingOnly && (
          <select
            value={decisionFilter}
            onChange={e => setDecisionFilter(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 bg-white"
          >
            <option value="">All decisions</option>
            {Object.entries(DECISION_STYLE).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        )}
      </div>

      {/* List — grouped by employee */}
      {loading ? (
        <div className="space-y-2">
          {[1,2,3].map(i => <div key={i} className="h-14 rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl bg-white border border-gray-100 shadow-sm p-12 text-center">
          <CheckCircle2 size={32} className="mx-auto text-emerald-300 mb-2" />
          <p className="text-gray-500 text-sm font-medium">
            {pendingOnly ? "No pending verifications — all clear!" : "No verifications found"}
          </p>
        </div>
      ) : (
        <EmployeeVerificationGroups
          items={items}
          onReview={setReviewTarget}
        />
      )}

      {/* Pagination */}
      {pagination && (pagination.pages ?? 1) > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">{pagination.total} total · page {page} of {pagination.pages}</p>
          <div className="flex gap-2">
            <Button variant="ghost" disabled={page <= 1} onClick={() => load(page - 1)} className="text-sm border border-gray-200">Previous</Button>
            <Button variant="ghost" disabled={page >= pagination.pages} onClick={() => load(page + 1)} className="text-sm border border-gray-200">Next</Button>
          </div>
        </div>
      )}

      {reviewTarget && (
        <ReviewModal
          item={reviewTarget}
          onClose={() => setReviewTarget(null)}
          onDone={() => done(() => setReviewTarget(null))}
        />
      )}
    </div>
  );
}
